import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { UploadOutlined, DeleteOutlined, PaperClipOutlined } from "@ant-design/icons";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomButton from "../../../components/CustomButton/CustomButton";
import CustomSeparator from "../../../components/Common/CustomSeparator";
import CheckRender from "../../../components/Common/CheckRender";
import CustomAlert from "../../../components/Common/CustomAlert";
import { FormGroup, Label, TextArea } from "../../../components/CustomForm/FormStyles";
import Colors from "../../../themes/Colors";
import IngresoService from "../../../services/ingreso/IngresoService";
import { validateUploadFile, buildAlertConfig, MAX_FILES_COUNT } from "../utils/HojaQuirurgicaUtils";
import { FORMATOS_ADJUNTOS_PERMITIDOS } from "../utils/IngresoConstant";

const ActualizarSolicitudHojaQuirurgicaModal = ({
  isOpen,
  onClose,
  solicitud,
  empresa,
  onUpdateSuccess,
  setLoading
}) => {
  const [comentarios, setComentarios] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showRequired, setShowRequired] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState(null);

  const fileInputRef = useRef(null);
  const COLOR_PRINCIPAL = empresa?.COLOR_PRINCIPAL || Colors.primaryPrimera;

  // Reset internal states when the modal is opened or closed
  useEffect(() => {
    if (isOpen) {
      setComentarios("");
      setSelectedFiles([]);
      setShowRequired(false);
      setShowAlert(false);
      setAlertConfig(null);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (selectedFiles.length + files.length > MAX_FILES_COUNT) {
      setAlertConfig(
        buildAlertConfig(
          "error",
          "Límite superado",
          `Solo se permite adjuntar un máximo de ${MAX_FILES_COUNT} documentos.`,
          () => setShowAlert(false)
        )
      );
      setShowAlert(true);
      return;
    }

    const invalidFiles = [];
    const validFiles = [];
    const duplicateFiles = [];

    files.forEach((file) => {
      // Validate duplicate files (by name and size)
      const isDuplicate = selectedFiles.some(
        (existingFile) => existingFile.name === file.name && existingFile.size === file.size
      );
      if (isDuplicate) {
        duplicateFiles.push(file.name);
        return;
      }

      // Validate format and size using the centralized utility
      const validation = validateUploadFile(file);
      if (!validation.isValid) {
        invalidFiles.push({ name: file.name, reason: validation.message });
      } else {
        validFiles.push(file);
      }
    });

    if (duplicateFiles.length > 0) {
      setAlertConfig(
        buildAlertConfig(
          "error",
          "Archivo duplicado",
          `Los siguientes archivos ya han sido adjuntados: ${duplicateFiles.join(", ")}.`,
          () => setShowAlert(false)
        )
      );
      setShowAlert(true);
      return;
    }

    if (invalidFiles.length > 0) {
      const messages = invalidFiles.map((f) => `${f.name}: ${f.reason}`).join("\n");
      setAlertConfig(
        buildAlertConfig(
          "error",
          "Archivo no válido",
          `No se pudieron cargar algunos archivos:\n${messages}`,
          () => setShowAlert(false)
        )
      );
      setShowAlert(true);
      return;
    }

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
    e.target.value = ""; // Reset value to allow uploading same file again
  };

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleUpdate = async () => {
    if (comentarios.trim() === "") {
      setShowRequired(true);
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("Comentarios", comentarios.trim());

      selectedFiles.forEach((file) => {
        formData.append("Documentos", file);
      });

      const response = await IngresoService.actualizarSolicitudHojaQuirurgica(
        solicitud?.numeroSolicitud,
        formData
      );

      if (response?.respuesta?.codigo === 200 || response?.data === true) {
        setAlertConfig({
          type: "success",
          title: "¡Éxito!",
          message: response?.respuesta?.mensaje || "Solicitud actualizada exitosamente.",
          onConfirm: () => {
            setShowAlert(false);
            onUpdateSuccess();
            onClose();
          }
        });
        setShowAlert(true);
      } else {
        throw new Error(response?.respuesta?.mensaje || "No se pudo actualizar la solicitud.");
      }
    } catch (error) {
      console.error("Error al actualizar solicitud:", error);
      setAlertConfig({
        type: "error",
        title: "¡Error!",
        message: error.message || "Ocurrió un error al actualizar la solicitud.",
        onConfirm: () => setShowAlert(false)
      });
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomModal isOpen={isOpen} onClose={onClose}>
      <CheckRender allowed={solicitud}>
        <div style={{ textAlign: "left", padding: "10px", maxHeight: "85vh", overflowY: "auto" }}>
          <h2 style={{ borderBottom: `2px solid ${COLOR_PRINCIPAL}`, paddingBottom: "10px", margin: "0 0 20px 0", color: COLOR_PRINCIPAL }}>
            Actualizar Solicitud
          </h2>

          {/* Details Card */}
          <div style={{ backgroundColor: "#f8f9fa", padding: "15px 20px", borderRadius: "8px", border: "1px solid #e9ecef", marginBottom: "25px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
              <div>
                <span style={{ fontSize: "11px", color: "#888", fontWeight: "bold", textTransform: "uppercase", display: "block" }}>
                  Número de Solicitud
                </span>
                <span style={{ fontSize: "15px", color: "#333", fontWeight: "bold" }}>
                  {solicitud?.numeroSolicitud}
                </span>
              </div>
              <div>
                <span style={{ fontSize: "11px", color: "#888", fontWeight: "bold", textTransform: "uppercase", display: "block" }}>
                  Número de Ingreso
                </span>
                <span style={{ fontSize: "15px", color: "#333", fontWeight: "bold" }}>
                  {solicitud?.numeroIngreso}
                </span>
              </div>
              <div>
                <span style={{ fontSize: "11px", color: "#888", fontWeight: "bold", textTransform: "uppercase", display: "block" }}>
                  Nombre del Afiliado
                </span>
                <span style={{ fontSize: "15px", color: "#333", fontWeight: "bold" }}>
                  {solicitud?.nombreAfiliado}
                </span>
              </div>
            </div>
          </div>

          {/* Section: Attach Files */}
          <div style={{ marginBottom: "25px" }}>
            <span style={{ fontSize: "14px", fontWeight: "bold", color: "#444", display: "block", marginBottom: "10px" }}>
              Documentos Adjuntos ({selectedFiles.length} / {MAX_FILES_COUNT})
            </span>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 15px",
                    backgroundColor: Colors.white,
                    borderRadius: "6px",
                    border: "1px solid #d9d9d9"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                    <PaperClipOutlined style={{ color: COLOR_PRINCIPAL, fontSize: "16px", flexShrink: 0 }} />
                    <span style={{ fontSize: "14px", color: "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {file.name}
                    </span>
                    <span style={{ fontSize: "12px", color: "#888", flexShrink: 0 }}>
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <DeleteOutlined
                    id={`btn-remove-file-${index}`}
                    style={{ color: Colors.huMyHealth || "#ff4d4f", fontSize: "18px", cursor: "pointer", marginLeft: "10px", flexShrink: 0 }}
                    onClick={() => handleRemoveFile(index)}
                  />
                </div>
              ))}
            </div>

            {selectedFiles.length < MAX_FILES_COUNT && (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept={FORMATOS_ADJUNTOS_PERMITIDOS}
                />
                <CustomButton
                  id="btn-adjuntar-documento"
                  label={
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                      <UploadOutlined />
                      <span>Adjuntar documentos</span>
                    </div>
                  }
                  onClick={() => fileInputRef.current?.click()}
                  bgColor={empresa?.COLOR_SECUNDARIO || Colors.huMyHealth}
                  textColor={Colors.white}
                  width="200px"
                  height="38px"
                  fontSize="14px"
                  weight="bold"
                />
              </div>
            )}
          </div>

          {/* Section: Comments */}
          <div style={{ marginBottom: "25px" }}>
            <FormGroup width="100%">
              <Label font={empresa?.LETRA_PRINCIPAL} style={{ display: "block", marginBottom: "8px", fontWeight: "bold", fontSize: "14px", color: "#444" }}>
                Comentarios <span style={{ color: Colors.huMyHealth || "#ff4d4f" }}>*</span>
              </Label>
              <TextArea
                id="comentarios-textarea"
                placeholder="Ingrese sus comentarios de actualización aquí..."
                value={comentarios}
                onChange={(e) => {
                  setComentarios(e.target.value);
                  if (e.target.value.trim() !== "") {
                    setShowRequired(false);
                  }
                }}
                width="100%"
                style={{
                  height: "120px",
                  borderColor: showRequired && comentarios.trim() === "" ? (Colors.huMyHealth || "#ff4d4f") : "#ccc",
                  borderWidth: "1px",
                  borderStyle: "solid"
                }}
              />
              {showRequired && comentarios.trim() === "" && (
                <span style={{ color: Colors.huMyHealth || "#ff4d4f", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  El comentario es requerido.
                </span>
              )}
            </FormGroup>
          </div>

          <CustomSeparator height="15px" />

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "15px", justifyContent: "center", width: "100%" }}>
            <CustomButton
              id="btn-actualizar-solicitud"
              label="Actualizar Solicitud"
              onClick={handleUpdate}
              bgColor={empresa?.COLOR_SECUNDARIO || Colors.huMyHealth}
              textColor={Colors.white}
              width="200px"
              height="45px"
              weight="bold"
              fontSize="14px"
            />
            <CustomButton
              id="btn-cancelar-actualizacion"
              label="Cancelar"
              onClick={onClose}
              bgColor={Colors.white}
              textColor={Colors.huMyHealth}
              border={`1px solid ${Colors.huMyHealth}`}
              width="200px"
              height="45px"
              weight="bold"
              fontSize="14px"
            />
          </div>
        </div>
      </CheckRender>

      <CheckRender allowed={showAlert}>
        <CustomAlert {...alertConfig} />
      </CheckRender>
    </CustomModal>
  );
};

ActualizarSolicitudHojaQuirurgicaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  solicitud: PropTypes.object,
  empresa: PropTypes.object,
  onUpdateSuccess: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired
};

export default ActualizarSolicitudHojaQuirurgicaModal;
